import { useState, useCallback } from "react";
import {
  HiX,
  HiOutlineCalendar,
  HiOutlineCreditCard,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi";
import { Box } from "rebass/styled-components";

import { Modal } from "MVVM/Components/Wrappers/Existing/modal";
import { analytics } from "MVVM/Analytics";
import {
  ModalContainer,
  ModalHeader,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  ModalBody,
  OptionCard,
  OptionIconWrapper,
  OptionTitle,
  OptionDescription,
  OptionsContainer,
  OfferCard,
  OfferIconWrapper,
  OfferTextContent,
  OfferTitle,
  OfferDescription,
  ModalFooter,
  PrimaryButton,
  SkipButton,
  NoteText,
} from "./styledComponents";

const CALENDAR_URL = "https://calendar.app.google/yMG5UTy74rbkK5e1A";

type Step = "intent" | "retention";

interface CancelRetentionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
}

export const CancelRetentionModal = ({
  isOpen,
  onClose,
  onSkip,
}: CancelRetentionModalProps) => {
  const [step, setStep] = useState<Step>("intent");

  const handleClose = useCallback(() => {
    setStep("intent");
    onClose();
  }, [onClose]);

  const handleManagePayment = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "payment" });
    setStep("intent");
    onSkip();
  }, [onSkip]);

  const handleOther = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "other" });
    setStep("intent");
    onSkip();
  }, [onSkip]);

  const handleCancel = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "cancel" });
    // Retention step temporarily hidden — restore by setting step to "retention".
    onSkip();
  }, [onSkip]);

  const handleSchedule = useCallback(() => {
    analytics.track("Cancel Retention Schedule Click");
    window.open(CALENDAR_URL, "_blank");
    handleClose();
  }, [handleClose]);

  const handleSkipRetention = useCallback(() => {
    analytics.track("Cancel Retention Skipped");
    setStep("intent");
    onSkip();
  }, [onSkip]);

  return (
    <Modal
      isModalOpen={isOpen}
      setModalOpen={(open: boolean) => !open && handleClose()}
      handleModalClose={undefined}
      disableOutsideClose={false}
    >
      <ModalContainer>
        {step === "retention" ? (
          <>
            <ModalHeader>
              <div>
                <HeaderTitle>Before you go...</HeaderTitle>
                <HeaderSubtitle>
                  We'd love to hear from you! Schedule a quick chat with our
                  product team and we'll extend your subscription as a thank
                  you.
                </HeaderSubtitle>
              </div>
              <CloseButton onClick={handleClose}>
                <HiX size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <OfferCard>
                <OfferIconWrapper>
                  <HiOutlineCalendar size={24} />
                </OfferIconWrapper>
                <OfferTextContent>
                  <OfferTitle>Get a free extension</OfferTitle>
                  <OfferDescription>
                    Book a 15-minute call with an Allstar Product Manager to
                    share your feedback. In return, we'll add extra time to your
                    subscription.
                  </OfferDescription>
                </OfferTextContent>
              </OfferCard>
              <NoteText>
                *Note — time will be added after completion of interview
              </NoteText>
            </ModalBody>

            <ModalFooter>
              <PrimaryButton onClick={handleSchedule}>
                Schedule a Call & Get Extension
              </PrimaryButton>
              <SkipButton onClick={handleSkipRetention}>
                No thanks, just take me to manage my subscription
              </SkipButton>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>
              <div>
                <HeaderTitle>Manage Subscription</HeaderTitle>
                <HeaderSubtitle>What would you like to do?</HeaderSubtitle>
              </div>
              <CloseButton onClick={handleClose}>
                <HiX size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <OptionsContainer>
                <OptionCard onClick={handleManagePayment}>
                  <OptionIconWrapper>
                    <HiOutlineCreditCard size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Manage payment details</OptionTitle>
                    <OptionDescription>
                      Update your card, billing info, or view invoices
                    </OptionDescription>
                  </Box>
                </OptionCard>

                <OptionCard onClick={handleOther}>
                  <OptionIconWrapper>
                    <HiOutlineQuestionMarkCircle size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Something else</OptionTitle>
                    <OptionDescription>
                      Change your plan, update preferences, or other options
                    </OptionDescription>
                  </Box>
                </OptionCard>

                <OptionCard onClick={handleCancel}>
                  <OptionIconWrapper>
                    <HiX size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Cancel my subscription</OptionTitle>
                    <OptionDescription>
                      We'd hate to see you go
                    </OptionDescription>
                  </Box>
                </OptionCard>
              </OptionsContainer>
            </ModalBody>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};
